import { useTranslation } from 'react-i18next';
import { TachoTabs } from './TachographPopup';
import { MouseEvent, useContext, useState } from 'react';
import { Box, Button, Chip, Menu, MenuItem, Stack } from '@mui/material';
import {
  DirectionsCarFilledOutlined,
  DirectionsCarOutlined,
  KeyboardArrowDown,
  Person2Outlined,
  PersonOutlineOutlined,
} from '@mui/icons-material';
import { ctxTachograph } from '@/contexts/TachographCtx';
import { TabValue } from '@/types';

export default function TachographCategorySelector({
  activeTab,
  setActiveTab,
  isPhone,
}: {
  activeTab?: TachoTabs;
  setActiveTab: (tab: TachoTabs) => void;
  isPhone?: boolean;
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const tachographCtx = useContext(ctxTachograph);

  if (!tachographCtx) return;

  function handleClick(event: MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleSelect(option: TachoTabs) {
    setActiveTab(option);
    handleClose();
  }

  function getLabelText() {
    switch (activeTab) {
      case 'TargetTab':
        return t('tachograph.category.vehicleData');
      case 'DriverTab':
        return t('tachograph.category.driverData');
    }
  }

  function getLabel() {
    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <span className="category-selection-text">{getLabelText()}</span>
        <KeyboardArrowDown fontSize="small" />
      </Stack>
    );
  }

  function getIcon() {
    switch (activeTab) {
      case 'TargetTab':
        return <DirectionsCarOutlined className="category-selector-icon target" />;
      case 'DriverTab':
        return <PersonOutlineOutlined className="category-selector-icon driver" />;
    }
  }

  function getClass() {
    switch (activeTab) {
      case 'TargetTab':
        return 'target';
      case 'DriverTab':
        return 'driver';
    }
  }

  return isPhone ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',
        gap: 2,
        margin: '0% 8%',
      }}
    >
      <Stack direction="column" spacing={2} mt={2} sx={{ width: '100%', marginTop: '5svh' }}>
        <Button
          className={`active-tab-selector target`}
          variant="outlined"
          color="primary"
          startIcon={<DirectionsCarFilledOutlined />}
          fullWidth
          sx={{
            borderRadius: 5,
            justifyContent: 'flex-start',
            textAlign: 'left',
            pl: 2.5,
            fontWeight: 600,
            borderWidth: 2,
            '& .MuiButton-startIcon': {
              mr: 1.5,
            },
          }}
          onClick={() => {
            setActiveTab('TargetTab');
            tachographCtx.setTab(TabValue.result);
          }}
          data-qa-element-id="select_vehicle_category_button"
        >
          {t('tachograph.category.vehicleDownload')}
        </Button>
        <Button
          className={`active-tab-selector driver`}
          variant="contained"
          color="secondary"
          fullWidth
          startIcon={<Person2Outlined />}
          sx={{
            borderRadius: 5,
            justifyContent: 'flex-start',
            textAlign: 'left',
            paddingRight: 0,
            pl: 2.5,
            '& .MuiButton-startIcon': {
              mr: 1.5,
            },
          }}
          onClick={() => {
            setActiveTab('DriverTab');
            tachographCtx.setTab(TabValue.result);
          }}
          data-qa-element-id="select_driver_category_button"
        >
          {t('tachograph.category.driverDownload')}
        </Button>
      </Stack>
    </Box>
  ) : (
    <div className="active-tab-selector-conatiner">
      <Chip
        className={`active-tab-selector ${getClass()}`}
        label={getLabel()}
        onClick={handleClick}
        clickable
        icon={getIcon()}
        sx={{ borderRadius: '16px' }}
      />
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem value="TargetTab" onClick={() => handleSelect('TargetTab')}>
          <div className="report-tab-selection-option">
            <DirectionsCarOutlined />
            <span>{t('tachograph.category.vehicleData')}</span>
          </div>
        </MenuItem>
        <MenuItem value="DriverTab" onClick={() => handleSelect('DriverTab')}>
          <div className="report-tab-selection-option">
            <PersonOutlineOutlined />
            <span>{t('tachograph.category.driverData')}</span>
          </div>
        </MenuItem>
      </Menu>
    </div>
  );
}
